import React from "react";
import { DIRECTION, SIZE, VARIENT } from "constant";
import { SelectIconContainer } from "components/common/Input/style";
import Icon from "components/common/Icon";

export function getStyle(
  disabled,
  errorMsg,
  variant,
  focused,
  size,
  theme,
  rounded,
  width,
  isMultiSelect,
  propMenuWidth,
  propMenuDirection,
  menuHeight
) {
  let selectStyle = getCommonStyle(
    disabled,
    errorMsg,
    variant,
    focused,
    size,
    theme,
    isMultiSelect
  );
  const borderRadius = rounded ? BORDER_RADIUS.rounded : BORDER_RADIUS.default;
  const menuWidth = propMenuWidth
    ? { width: propMenuWidth }
    : { width: "max-content", minWidth: "170px" };
  const menuDirection = propMenuDirection
    ? propMenuDirection === DIRECTION.ltr
      ? { left: 0 }
      : { right: 0 }
    : {};
  if (isMultiSelect) {
    const additionalMultiStyle = getMultiSelectStyle(size);
    selectStyle = { ...selectStyle, ...additionalMultiStyle };
    return getMultiSelectComponentStyle(selectStyle, width, menuWidth);
  } else {
    return getSelectComponentStyle(
      selectStyle,
      borderRadius,
      width,
      menuWidth,
      menuDirection,
      menuHeight
    );
  }
}

function getSelectComponentStyle(
  selectStyle,
  borderRadius,
  width,
  menuWidth,
  menuDirection,
  menuHeight
) {
  return {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (styles) => ({
      ...styles,
      marginTop: "1px",
      top: "auto",
      ...menuWidth,
      ...menuDirection,
    }),
    clearIndicator: (styles) => ({
      ...styles,
      display: "none",
      padding: "0px",
    }),
    container: (styles) => ({
      ...styles,
      maxWidth: width,
      "&:focus": { outline: "none" },
    }),
    control: (styles) => ({
      ...styles,
      paddingInlineStart: selectStyle.controlPaddingInline,
      paddingInlineEnd: selectStyle.controlPaddingInline,
      boxShadow: "none",
      backgroundColor: selectStyle.backgroundColor,
      borderColor: selectStyle.borderColor,
      minHeight: "auto",
      lineHeight: selectStyle.lineHeight,
      paddingBlockStart: selectStyle.controlPaddingBlock,
      paddingBlockEnd: selectStyle.controlPaddingBlock,
      flexWrap: "nowrap",
      borderRadius: borderRadius,
      width: "initial",
      maxWidth: "initial",
      "&:hover": { borderColor: "none", cursor: "pointer" },
    }),
    indicatorSeparator: (styles) => ({ ...styles, display: "none" }),
    indicatorsContainer: (styles) => ({ ...styles }),
    valueContainer: (styles) => ({
      ...styles,
      height: selectStyle.lineHeight,
      padding: 0,
      flexWrap: "nowrap",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: selectStyle.textColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      margin: "0",
      overflow: "hidden",
      transform: "none",
      position: "initial",
      maxWidth: "100%",
    }),
    menuList: (styles) => ({
      ...styles,
      padding: "0px",
      maxHeight: menuHeight ? menuHeight : "275px",
      display: "flex",
      flexDirection: "column",
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: selectStyle.noOptionsMessageColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
    }),
    input: (styles) => ({
      ...styles,
      padding: "0 0 1px 0 !important",
      margin: "0",
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      "& div div": { fontSize: `${selectStyle.fontSize}px !important` },
    }),
    placeholder: (styles) => ({
      ...styles,
      color: selectStyle.placeholderColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      margin: "0",
      overflow: "hidden",
      transform: "none",
      top: 0,
      maxWidth: "100%",
    }),
    option: (styles, { data, isDisabled, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected ? selectStyle.optionBackgroundColor : null,
        color: isDisabled
          ? selectStyle.textColor
          : isSelected
          ? selectStyle.textColor
          : selectStyle.textColor,
        cursor: isDisabled ? CURSORS.notAllowed : CURSORS.pointer,
        ":active": {
          ...styles[":active"],
          backgroundColor:
            !isDisabled &&
            (isSelected ? data.color : selectStyle.optionBackgroundColor),
        },
        ":hover": {
          backgroundColor: selectStyle.optionBackgroundColor,
        },
        fontFamily: "arimo, arial",
        fontSize: selectStyle.fontSize,
        minHeight: "45px",
        lineHeight: selectStyle.lineHeight,
        padding: "12px 10px",
        borderBottom: "1px solid " + selectStyle.optionBorderColor,
      };
    },
  };
}

function getMultiSelectComponentStyle(
  selectStyle,
  width,
  isEmptyVariant,
  menuWidth
) {
  return {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (styles) => ({
      ...styles,
      marginTop: "1px",
      maxWidth: menuWidth ? menuWidth : "235px",
      minWidth: "170px",
      top: "auto",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      display: "none",
      padding: "0px",
    }),
    container: (styles) => ({
      ...styles,
      maxWidth: width,
      lineHeight: selectStyle.chipVerticalMargin,
      borderRadius: BORDER_RADIUS.default,
      borderColor: selectStyle.borderColor,
      borderWidth: 1,
      borderStyle: "solid",
      flexWrap: "wrap",
      "&:focus": { outline: "none" },
    }),
    control: (styles) => ({
      ...styles,
      paddingInlineStart: !isEmptyVariant && selectStyle.controlPaddingInline,
      paddingInlineEnd: selectStyle.controlPaddingInline,
      boxShadow: "none",
      borderWidth: 0,
      minHeight: selectStyle.multiValueHeight || "auto",
      paddingBlockStart: selectStyle.controlPaddingBlock,
      paddingBlockEnd: selectStyle.controlPaddingBlock,
      flexWrap: "nowrap",
      width: isEmptyVariant ? "fit-content" : "initial",
      maxWidth: isEmptyVariant ? "100%" : "initial",
      "&:hover": { borderColor: "none", cursor: "pointer" },
    }),
    indicatorSeparator: (styles) => ({ ...styles, display: "none" }),
    indicatorsContainer: (styles) => ({ ...styles }),
    valueContainer: (styles) => ({
      ...styles,
      padding: 0,
      flexWrap: "wrap",
      marginInlineEnd: "10px",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: selectStyle.textColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      margin: "0",
      overflow: "hidden",
      transform: "none",
      position: "initial",
      maxWidth: "100%",
    }),
    menuList: (styles) => ({
      ...styles,
      padding: "0px",
      maxHeight: "275px",
      display: "flex",
      flexDirection: "column",
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: selectStyle.noOptionsMessageColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
    }),
    input: (styles) => ({
      ...styles,
      padding: "0 0 1px 0 !important",
      margin: "0",
      fontFamily: "arimo, arial",
      minWidth: "20px !important",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      "& div div": { fontSize: `${selectStyle.fontSize}px !important` },
    }),
    placeholder: (styles) => ({
      ...styles,
      color: selectStyle.placeholderColor,
      fontFamily: "arimo, arial",
      fontSize: selectStyle.fontSize,
      lineHeight: selectStyle.lineHeight,
      margin: "0",
      overflow: "hidden",
      transform: "none",
      top: 0,
      [isEmptyVariant && "maxWidth"]: isEmptyVariant && "100%",
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected
          ? selectStyle.optionBackgroundColor
          : isFocused
          ? selectStyle.optionBackgroundColor
          : null,
        color: isDisabled
          ? selectStyle.textColor
          : isSelected
          ? selectStyle.textColor
          : selectStyle.textColor,
        cursor: isDisabled ? CURSORS.notAllowed : CURSORS.pointer,
        ":active": {
          ...styles[":active"],
          backgroundColor:
            !isDisabled &&
            (isSelected ? data.color : selectStyle.optionBackgroundColor),
        },
        fontFamily: "arimo, arial",
        fontSize: selectStyle.fontSize,
        minHeight: "45px",
        lineHeight: selectStyle.lineHeight,
        padding: "10px 10px",
        borderBottom: "1px solid " + selectStyle.optionBorderColor,
      };
    },
  };
}

export function boldSearchMatch(label, searchValue) {
  let boldedLetters = [];
  let boldedWord = "";
  if (searchValue.length > 0) {
    let unboldedResult = label;
    let lastIndexOfSearch = 0;
    let startIndex = label
      .toLowerCase()
      .indexOf(searchValue.toLowerCase(), lastIndexOfSearch);
    while (startIndex >= 0) {
      let endIndex = startIndex + searchValue.length;
      lastIndexOfSearch = endIndex;
      if (label.charAt(endIndex) === " ") {
        endIndex++;
      }
      boldedLetters.push({ start: startIndex, end: endIndex });
      unboldedResult = unboldedResult.substring(endIndex);
      startIndex = label
        .toLowerCase()
        .indexOf(searchValue.toLowerCase(), lastIndexOfSearch);
    }
    for (let i = 0; i < boldedLetters.length; i++) {
      if (i === 0 && boldedLetters[i].start > 0) {
        boldedWord += label.substring(0, boldedLetters[i].start);
      }
      if (i + 1 < boldedLetters.length) {
        boldedWord +=
          `<b>${label.substring(
            boldedLetters[i].start,
            boldedLetters[i].end
          )}</b>` +
          label.substring(boldedLetters[i].end, boldedLetters[i + 1].start);
      } else {
        boldedWord +=
          `<b>${label.substring(
            boldedLetters[i].start,
            boldedLetters[i].end
          )}</b>` + label.substring(boldedLetters[i].end);
      }
    }
    return <span dangerouslySetInnerHTML={{ __html: boldedWord }} />;
  }
  return label;
}

function getCommonStyle(
  disabled,
  errorMsg,
  variant,
  focused,
  size,
  theme,
  isMultiSelect
) {
  let controlPaddingInline,
    backgroundColor,
    borderColor,
    lineHeight,
    controlPaddingBlock,
    fontSize,
    chipVerticalMargin,
    multiValueHeight;

  let textColor = theme.colors.gray.charcoal;
  let noOptionsMessageColor = theme.colors.gray.aluminium;
  let placeholderColor = theme.colors.gray.aluminium;
  let optionBorderColor = theme.colors.gray.gray200;
  let optionBackgroundColor = theme.colors.gray.gray200;

  if (!isMultiSelect) {
    switch (variant) {
      case VARIENT.outlined:
        borderColor =
          !disabled && errorMsg
            ? theme.colors.red.salmon
            : theme.colors.gray.gray500;
        backgroundColor = theme.colors.white;
        break;
      case VARIENT.contained:
        borderColor = theme.colors.transparent;
        backgroundColor =
          !disabled && errorMsg
            ? theme.colors.red.blush
            : theme.colors.gray.gray200;
        break;
      case VARIENT.empty:
        borderColor = theme.colors.transparent;
        backgroundColor = theme.colors.transparent;
        break;
      default:
        break;
    }
  } else {
    borderColor =
      !disabled && errorMsg
        ? theme.colors.red.salmon
        : theme.colors.gray.gray500;
  }

  if (
    focused &&
    !disabled &&
    (variant === VARIENT.outlined || variant === VARIENT.text || isMultiSelect)
  ) {
    borderColor = theme.colors.p1;
  } else if (focused && variant === VARIENT.contained) {
    backgroundColor = theme.colors.gray.gray300;
    borderColor = theme.colors.gray.gray300;
  }

  switch (size) {
    case SIZE.small:
      fontSize = FONT_SIZE.small;
      controlPaddingInline = CONTROL_PADDING_INLINE.small;
      lineHeight = LINE_HEIGHT.small;
      controlPaddingBlock = CONTROL_PADDING_BLOCK.small;
      break;
    case SIZE.smallh:
      fontSize = FONT_SIZE.smallh;
      controlPaddingInline = CONTROL_PADDING_INLINE.smallh;
      lineHeight = LINE_HEIGHT.smallh;
      controlPaddingBlock = CONTROL_PADDING_BLOCK.smallh;
      break;
    case SIZE.medium:
      fontSize = FONT_SIZE.medium;
      controlPaddingInline = CONTROL_PADDING_INLINE.medium;
      lineHeight = LINE_HEIGHT.medium;
      chipVerticalMargin = "3px";
      multiValueHeight = "32px";
      if (isMultiSelect) {
        controlPaddingBlock = CONTROL_PADDING_BLOCK.small;
      } else {
        controlPaddingBlock = CONTROL_PADDING_BLOCK.medium;
      }
      break;
    case SIZE.large:
      fontSize = FONT_SIZE.large;
      controlPaddingInline = CONTROL_PADDING_INLINE.large;
      lineHeight = LINE_HEIGHT.large;
      chipVerticalMargin = "4px";
      multiValueHeight = "40px";

      if (isMultiSelect) {
        controlPaddingBlock = "4px";
      } else {
        controlPaddingBlock = CONTROL_PADDING_BLOCK.large;
      }
      break;
    case SIZE.xl:
      fontSize = FONT_SIZE.xl;
      controlPaddingInline = CONTROL_PADDING_INLINE.xl;
      lineHeight = LINE_HEIGHT.xl;
      chipVerticalMargin = "4px";
      multiValueHeight = "48px";

      if (isMultiSelect) {
        controlPaddingBlock = "4px";
      } else {
        controlPaddingBlock = CONTROL_PADDING_BLOCK.xl;
      }
      break;
    case SIZE.xlh:
      fontSize = FONT_SIZE.xlh;
      controlPaddingInline = CONTROL_PADDING_INLINE.xlh;
      lineHeight = LINE_HEIGHT.xlh;
      controlPaddingBlock = CONTROL_PADDING_BLOCK.xlh;
      break;
    default:
      fontSize = FONT_SIZE.small;
      controlPaddingInline = CONTROL_PADDING_INLINE.small;
      lineHeight = LINE_HEIGHT.small;
      controlPaddingBlock = CONTROL_PADDING_BLOCK.small;
      break;
  }

  return {
    controlPaddingInline,
    backgroundColor,
    borderColor,
    lineHeight,
    controlPaddingBlock,
    fontSize,
    chipVerticalMargin,
    multiValueHeight,
    optionBackgroundColor,
    optionBorderColor,
    placeholderColor,
    noOptionsMessageColor,
    textColor,
  };
}

function getMultiSelectStyle(size) {
  let chipVerticalMargin, multiValueHeight;
  switch (size) {
    case SIZE.medium:
      chipVerticalMargin = "3px";
      multiValueHeight = "32px";
      break;
    case SIZE.large:
      chipVerticalMargin = "4px";
      multiValueHeight = "40px";
      break;
    case SIZE.xl:
      chipVerticalMargin = "4px";
      multiValueHeight = "48px";
      break;
  }
  return { chipVerticalMargin, multiValueHeight };
}

export function getDropdownIndicator(
  iconName,
  iconSize,
  iconColor,
  iconInlineMargin
) {
  return (
    <SelectIconContainer iconInlineMargin={iconInlineMargin}>
      <Icon name={iconName} size={iconSize} color={iconColor}></Icon>
    </SelectIconContainer>
  );
}

export function getMobileDropdownIndicator(
  iconName,
  iconSize,
  iconColor,
  iconInlineMargin,
  onTouchStart
) {
  return (
    <SelectIconContainer
      iconInlineMargin={iconInlineMargin}
      onTouchStart={onTouchStart}
    >
      <Icon name={iconName} size={iconSize} color={iconColor}></Icon>
    </SelectIconContainer>
  );
}

const CURSORS = { notAllowed: "not-allowed", pointer: "pointer" };
const FONT_SIZE = {
  small: "12px",
  smallh: "14px",
  medium: "14px",
  large: "16px",
  xl: "18px",
  xlh: "20px",
};
const LINE_HEIGHT = {
  small: "16px",
  smallh: "20px",
  medium: "20px",
  large: "24px",
  xl: "24px",
  xlh: "24px",
};
const CONTROL_PADDING_INLINE = {
  small: "7px",
  smallh: "7px",
  medium: "7px",
  large: "5px",
  xl: "15px",
  xlh: "15px",
};
const CONTROL_PADDING_BLOCK = {
  small: "3px",
  smallh: "3px",
  medium: "5px",
  large: "7px",
  xl: "11px",
  xlh: "13px",
};
const BORDER_RADIUS = { default: "4px", rounded: "16px" };
